/**
 * Promotion Template Gallery
 * A beautiful modal gallery to select pre-built promotion templates
 * Based on design patterns from SequenceTemplateGallery
 */

import React, { useState, useMemo } from "react";
import {
  Modal as AntModal,
  Row,
  Col,
  Card,
  Typography,
  Space,
  Tag,
  Input,
  Button,
  Empty,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  HeartOutlined,
  RocketOutlined,
  GiftOutlined,
  UsergroupAddOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  StarOutlined,
  AppstoreOutlined,
  FireOutlined,
  CrownOutlined,
  TeamOutlined,
  CreditCardOutlined,
  BellOutlined,
  WarningOutlined,
  ReloadOutlined,
  ExportOutlined,
  SmileOutlined,
  BookOutlined,
  DollarOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  ALL_PROMOTION_TEMPLATES,
  PROMOTION_TEMPLATE_CATEGORIES,
  PromotionTemplate,
} from "../promotionTemplates";

// Cast Modal to any to avoid TypeScript issues with Emotion CSS-in-JS
const Modal = AntModal as any;

const { Title, Text, Paragraph } = Typography;

// Icon mapping for dynamic icon rendering
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  CalendarOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  HeartOutlined,
  RocketOutlined,
  GiftOutlined,
  UsergroupAddOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  StarOutlined,
  AppstoreOutlined,
  FireOutlined,
  CrownOutlined,
  TeamOutlined,
  CreditCardOutlined,
  BellOutlined,
  WarningOutlined,
  ReloadOutlined,
  ExportOutlined,
  SmileOutlined,
  BookOutlined,
  DollarOutlined,
  SyncOutlined,
};

// Difficulty badge colors
const DIFFICULTY_COLORS = {
  beginner: { bg: "#dcfce7", text: "#15803d", border: "#86efac" },
  intermediate: { bg: "#fef3c7", text: "#b45309", border: "#fcd34d" },
  advanced: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
};

// Category type colors
const CATEGORY_COLORS: Record<string, string> = {
  seasonal: "#8B5CF6",
  flash_sale: "#EF4444",
  milestone: "#F59E0B",
  referral: "#10B981",
  win_back: "#EC4899",
  upgrade: "#3B82F6",
  first_time: "#06B6D4",
  credit_rewards: "#F59E0B",
};

interface PromotionTemplateGalleryProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (template: PromotionTemplate) => void;
}

const PromotionTemplateGallery: React.FC<PromotionTemplateGalleryProps> = ({
  visible,
  onClose,
  onSelect,
}) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<PromotionTemplate | null>(null);

  // Filter templates based on category and search
  const filteredTemplates = useMemo(() => {
    return ALL_PROMOTION_TEMPLATES.filter((template) => {
      const matchesCategory =
        selectedCategory === "all" || template.category === selectedCategory;
      const matchesSearch =
        searchQuery === "" ||
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        );
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  // Render icon component dynamically
  const renderIcon = (iconName: string, style: React.CSSProperties = {}) => {
    const IconComponent = ICON_MAP[iconName];
    return IconComponent ? <IconComponent style={style} /> : null;
  };

  // Handle template selection
  const handleTemplateClick = (template: PromotionTemplate) => {
    setSelectedTemplate(template);
  };

  // Handle use template
  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelect(selectedTemplate);
      setSelectedTemplate(null);
      setSelectedCategory("all");
      setSearchQuery("");
      onClose();
    }
  };

  // Template Card Component
  const TemplateCard = ({ template }: { template: PromotionTemplate }) => {
    const isSelected = selectedTemplate?.id === template.id;
    const difficultyStyle = DIFFICULTY_COLORS[template.difficulty];
    const categoryColor = CATEGORY_COLORS[template.category] || "#6b7280";

    return (
      <Card
        hoverable
        onClick={() => handleTemplateClick(template)}
        style={{
          height: "100%",
          border: isSelected ? `2px solid ${categoryColor}` : "1px solid #e5e7eb",
          borderRadius: 12,
          transition: "all 0.2s ease",
          boxShadow: isSelected
            ? `0 4px 12px ${categoryColor}33`
            : "0 1px 3px rgba(0,0,0,0.08)",
          background: isSelected ? `${categoryColor}08` : "#fff",
          position: "relative",
        }}
        bodyStyle={{ padding: 16 }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${categoryColor}20, ${categoryColor}40)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {renderIcon(template.icon, { fontSize: 24, color: categoryColor })}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              strong
              style={{
                fontSize: 15,
                display: "block",
                lineHeight: 1.3,
                marginBottom: 4,
              }}
            >
              {template.name}
            </Text>
            <Space size={4}>
              <Tag
                style={{
                  borderRadius: 4,
                  border: `1px solid ${categoryColor}40`,
                  background: `${categoryColor}15`,
                  color: categoryColor,
                  fontSize: 11,
                  padding: "0 6px",
                  margin: 0,
                }}
              >
                {template.category.replace("_", " ")}
              </Tag>
              <Tag
                style={{
                  borderRadius: 4,
                  border: `1px solid ${difficultyStyle.border}`,
                  background: difficultyStyle.bg,
                  color: difficultyStyle.text,
                  fontSize: 11,
                  padding: "0 6px",
                  margin: 0,
                }}
              >
                {template.difficulty}
              </Tag>
            </Space>
          </div>
        </div>

        {/* Description */}
        <Paragraph
          ellipsis={{ rows: 2 }}
          style={{
            fontSize: 13,
            color: "#6b7280",
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          {template.description}
        </Paragraph>

        {/* Stats */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 0",
            borderTop: "1px solid #f3f4f6",
          }}
        >
          <Space size={16}>
            <Tooltip title={template.defaults.discount.currency === "credits" ? "Credit Reward" : "Discount"}>
              <Space size={4}>
                {template.defaults.discount.currency === "credits" ? (
                  <StarOutlined style={{ color: "#F59E0B", fontSize: 14 }} />
                ) : (
                  <DollarOutlined style={{ color: "#9ca3af", fontSize: 14 }} />
                )}
                <Text style={{ fontSize: 13, color: template.defaults.discount.currency === "credits" ? "#F59E0B" : "#6b7280" }}>
                  {template.defaults.discount.currency === "credits"
                    ? `+${template.defaults.discount.discountValue}`
                    : `${template.defaults.discount.discountValue}%`}
                </Text>
              </Space>
            </Tooltip>
            <Tooltip title="Setup time">
              <Space size={4}>
                <ClockCircleOutlined style={{ color: "#9ca3af", fontSize: 14 }} />
                <Text style={{ fontSize: 13, color: "#6b7280" }}>
                  {template.setupTime}
                </Text>
              </Space>
            </Tooltip>
          </Space>
          {template.defaults.gamification.type !== "none" && (
            <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>
              Gamified
            </Tag>
          )}
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <div
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: categoryColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleOutlined style={{ color: "#fff", fontSize: 14 }} />
          </div>
        )}
      </Card>
    );
  };

  // Template Detail Panel
  const TemplateDetailPanel = ({ template }: { template: PromotionTemplate | null }) => {
    if (!template) {
      return (
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
            textAlign: "center",
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Text style={{ color: "#9ca3af", display: "block", marginBottom: 8 }}>
                  Select a template to view details
                </Text>
                <Text style={{ fontSize: 12, color: "#d1d5db" }}>
                  Choose from {ALL_PROMOTION_TEMPLATES.length}+ pre-built promotions based on best practices
                </Text>
              </div>
            }
          />
        </div>
      );
    }

    const categoryColor = CATEGORY_COLORS[template.category] || "#6b7280";

    return (
      <div style={{ padding: 24, height: "100%", overflow: "auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                background: `linear-gradient(135deg, ${categoryColor}20, ${categoryColor}40)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {renderIcon(template.icon, { fontSize: 28, color: categoryColor })}
            </div>
            <div>
              <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
                {template.name}
              </Title>
              <Space>
                <Tag color={categoryColor}>{template.category.replace("_", " ")}</Tag>
                <Tag>{template.difficulty}</Tag>
                {template.defaults.gamification.type !== "none" && (
                  <Tag color="purple">Gamified</Tag>
                )}
              </Space>
            </div>
          </div>
          <Paragraph style={{ color: "#6b7280", marginBottom: 0 }}>
            {template.description}
          </Paragraph>
        </div>

        {/* Strategy */}
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Strategy
          </Text>
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <Text style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.6 }}>
              {template.strategy}
            </Text>
          </div>
        </div>

        {/* Expected Outcomes */}
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Expected Outcomes
          </Text>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            {template.expectedOutcomes.map((outcome, index) => (
              <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <CheckCircleOutlined style={{ color: "#10b981", marginTop: 3, fontSize: 14 }} />
                <Text style={{ fontSize: 13, color: "#4b5563" }}>{outcome}</Text>
              </div>
            ))}
          </Space>
        </div>

        {/* Best Practices */}
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Best Practices
          </Text>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            {template.bestPractices.map((practice, index) => (
              <div key={index} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <StarOutlined style={{ color: "#f59e0b", marginTop: 3, fontSize: 14 }} />
                <Text style={{ fontSize: 13, color: "#4b5563" }}>{practice}</Text>
              </div>
            ))}
          </Space>
        </div>

        {/* Pre-filled Values Preview */}
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Pre-filled Values
          </Text>
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: 12,
            }}
          >
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text style={{ fontSize: 13, color: "#6b7280" }}>Type:</Text>
                <Text style={{ fontSize: 13, fontWeight: 500 }}>{template.defaults.type}</Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text style={{ fontSize: 13, color: "#6b7280" }}>Discount:</Text>
                <Text style={{ fontSize: 13, fontWeight: 500 }}>
                  {template.defaults.discount.discountValue}%
                </Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text style={{ fontSize: 13, color: "#6b7280" }}>Style:</Text>
                <Text style={{ fontSize: 13, fontWeight: 500 }}>{template.defaults.displayStyle}</Text>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text style={{ fontSize: 13, color: "#6b7280" }}>Countdown:</Text>
                <Text style={{ fontSize: 13, fontWeight: 500 }}>
                  {template.defaults.urgency.showCountdown ? "Yes" : "No"}
                </Text>
              </div>
              {template.defaults.gamification.type !== "none" && (
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text style={{ fontSize: 13, color: "#6b7280" }}>Gamification:</Text>
                  <Text style={{ fontSize: 13, fontWeight: 500 }}>
                    {template.defaults.gamification.type.replace("_", " ")}
                  </Text>
                </div>
              )}
            </Space>
          </div>
        </div>

        {/* Content Preview */}
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Content Preview
          </Text>
          <div
            style={{
              background: `linear-gradient(135deg, ${template.defaults.styling.primaryColor}15, ${template.defaults.styling.secondaryColor}15)`,
              border: `1px solid ${template.defaults.styling.primaryColor}30`,
              borderRadius: 8,
              padding: 16,
            }}
          >
            {template.defaults.content.en.badge && (
              <Tag
                style={{
                  background: template.defaults.styling.primaryColor,
                  color: "#fff",
                  border: "none",
                  marginBottom: 8,
                }}
              >
                {template.defaults.content.en.badge}
              </Tag>
            )}
            <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
              {template.defaults.content.en.headline}
            </Title>
            <Text style={{ fontSize: 13, color: "#4b5563", display: "block", marginBottom: 8 }}>
              {template.defaults.content.en.subheadline}
            </Text>
            <Button
              type="primary"
              size="small"
              style={{
                background: template.defaults.styling.primaryColor,
                borderColor: template.defaults.styling.primaryColor,
              }}
            >
              {template.defaults.content.en.ctaText}
            </Button>
          </div>
        </div>

        {/* Tags */}
        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Tags
          </Text>
          <Space wrap size={4}>
            {template.tags.map((tag) => (
              <Tag key={tag} style={{ borderRadius: 4 }}>
                {tag}
              </Tag>
            ))}
          </Space>
        </div>

        {/* Best For */}
        <div>
          <Text strong style={{ display: "block", marginBottom: 8, color: "#374151" }}>
            Best For
          </Text>
          <Space wrap size={4}>
            {template.bestFor.map((item) => (
              <Tag key={item} color="blue" style={{ borderRadius: 4 }}>
                {item}
              </Tag>
            ))}
          </Space>
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={null}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      bodyStyle={{ padding: 0, height: "85vh", overflow: "hidden" }}
      style={{ top: 40 }}
      destroyOnClose
    >
      <div style={{ display: "flex", height: "100%" }}>
        {/* Left Panel - Template List */}
        <div
          style={{
            width: "60%",
            borderRight: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div style={{ padding: 20, borderBottom: "1px solid #e5e7eb" }}>
            <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
              Choose a Promotion Template
            </Title>
            <Text style={{ color: "#6b7280" }}>
              {ALL_PROMOTION_TEMPLATES.length}+ pre-built promotions based on best practices from successful SaaS companies
            </Text>
          </div>

          {/* Filters */}
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #f3f4f6" }}>
            {/* Category Pills */}
            <Space wrap size={8} style={{ marginBottom: 16 }}>
              <Button
                type={selectedCategory === "all" ? "primary" : "default"}
                size="small"
                onClick={() => setSelectedCategory("all")}
                style={{ borderRadius: 6 }}
              >
                <Space size={6}>
                  <AppstoreOutlined />
                  <span>All ({ALL_PROMOTION_TEMPLATES.length})</span>
                </Space>
              </Button>
              {PROMOTION_TEMPLATE_CATEGORIES.map((category) => {
                const CategoryIcon = ICON_MAP[category.icon];
                return (
                  <Button
                    key={category.id}
                    type={selectedCategory === category.id ? "primary" : "default"}
                    size="small"
                    onClick={() => setSelectedCategory(category.id)}
                    style={{
                      borderRadius: 6,
                      borderColor:
                        selectedCategory === category.id ? category.color : undefined,
                      background:
                        selectedCategory === category.id ? category.color : undefined,
                    }}
                  >
                    <Space size={6}>
                      {CategoryIcon && <CategoryIcon />}
                      <span>{category.name} ({category.templates.length})</span>
                    </Space>
                  </Button>
                );
              })}
            </Space>

            {/* Search */}
            <Input
              placeholder="Search templates..."
              prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </div>

          {/* Template Grid */}
          <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
            {filteredTemplates.length > 0 ? (
              <Row gutter={[16, 16]}>
                {filteredTemplates.map((template) => (
                  <Col key={template.id} xs={24} sm={12}>
                    <TemplateCard template={template} />
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty
                description="No templates found"
                style={{ marginTop: 60 }}
              />
            )}
          </div>
        </div>

        {/* Right Panel - Template Details */}
        <div
          style={{
            width: "40%",
            display: "flex",
            flexDirection: "column",
            background: "#fff",
          }}
        >
          <div style={{ flex: 1, overflow: "auto" }}>
            <TemplateDetailPanel template={selectedTemplate} />
          </div>

          {/* Action Footer */}
          <div
            style={{
              padding: 16,
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
            }}
          >
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              disabled={!selectedTemplate}
              onClick={handleUseTemplate}
              style={{
                background: selectedTemplate
                  ? CATEGORY_COLORS[selectedTemplate.category]
                  : undefined,
                borderColor: selectedTemplate
                  ? CATEGORY_COLORS[selectedTemplate.category]
                  : undefined,
              }}
            >
              Use This Template
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PromotionTemplateGallery;
